import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Sidebar from '../../Layout/Sidebar';
import LanguageSelector from '../../Layout/LanguageSelector';
import JobSeekers from './JobSeekers';
import TaskManagement from './TaskManagement';
import AdminArchives from './AdminArchives';
import Users from './Users';
import OrderConfigManager from './OrderConfigManager';
import NotificationsPanel from '../NotificationsPanel';
import ChatPanel from '../ChatPanel';
import Profile from '../Profile';
import api from '../../../utils/api';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('users');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    const pollBadges = async () => {
      try {
        const [notifRes, chatRes] = await Promise.all([
          api.get('/notifications/unread-count'),
          api.get('/chat/unread-count')
        ]);
        if (notifRes.data?.data?.count !== undefined) setNotifUnread(notifRes.data.data.count);
        if (chatRes.data?.data?.count !== undefined) setChatUnread(chatRes.data.data.count);
      } catch { /* silent */ }
    };
    pollBadges();
    const interval = setInterval(pollBadges, 30000);
    return () => clearInterval(interval);
  }, []);

  const menuItems = [
    {
      label: t('admin.users', 'Users'),
      icon: '👥',
      path: '/admin',
      active: activeTab === 'users',
      onClick: () => setActiveTab('users')
    },
    {
      label: t('admin.taskManagement', 'Task Management'),
      icon: '📋',
      path: '/admin',
      active: activeTab === 'tasks',
      onClick: () => setActiveTab('tasks')
    },
    {
      label: t('admin.jobSeekers', 'Job Seekers'),
      icon: '📝',
      path: '/admin',
      active: activeTab === 'jobseekers',
      onClick: () => setActiveTab('jobseekers')
    },
    {
      label: t('dashboard.archives', 'Archives'),
      icon: '📁',
      path: '/admin',
      active: activeTab === 'archives',
      onClick: () => setActiveTab('archives')
    },
    {
      label: t('admin.orderConfig', 'Order Config'),
      icon: '⚙️',
      path: '/admin',
      active: activeTab === 'orderconfig',
      onClick: () => setActiveTab('orderconfig')
    },
    {
      label: t('dashboard.notifications', 'Notifications'),
      icon: '🔔',
      path: '/admin',
      active: activeTab === 'notifications',
      onClick: () => setActiveTab('notifications'),
      badge: notifUnread
    },
    {
      label: t('dashboard.chat', 'Chat'),
      icon: '💬',
      path: '/admin',
      active: activeTab === 'chat',
      onClick: () => setActiveTab('chat'),
      badge: chatUnread
    },
    {
      label: t('dashboard.profile', 'Profile'),
      icon: '👤',
      path: '/admin',
      active: activeTab === 'profile',
      onClick: () => setActiveTab('profile')
    }
  ];

  return (
    <div className="admin-dashboard">
      <Sidebar 
        menuItems={menuItems} 
        userRole="admin" 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="dashboard-main">
        <div className="mobile-header">
          <button 
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger-icon">☰</span>
          </button>
          <h1 className="mobile-title">{t('dashboard.adminDashboard', 'Admin Dashboard')}</h1>
          <div className="header-language-selector">
            <LanguageSelector />
          </div>
        </div>
        <div className="dashboard-container">
          <div className="dashboard-header-wrapper">
            <h1 className="desktop-title">{t('dashboard.adminDashboard', 'Admin Dashboard')}</h1>
            <div className="header-language-selector desktop-only">
              <LanguageSelector />
            </div>
          </div>
          
          <div className="dashboard-content">
            {activeTab === 'users' && <Users />}
            {activeTab === 'tasks' && <TaskManagement />}
            {activeTab === 'jobseekers' && <JobSeekers />}
            {activeTab === 'archives' && <AdminArchives />}
            {activeTab === 'orderconfig' && <OrderConfigManager />}
            {activeTab === 'notifications' && <NotificationsPanel onUnreadCountChange={setNotifUnread} />}
            {activeTab === 'chat' && <ChatPanel onUnreadCountChange={setChatUnread} />}
            {activeTab === 'profile' && <Profile userRole="admin" />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

